import React from "react";
import { useTranslation } from "react-i18next";
import { Columns, Page, Stack } from "components/layout/reusableLayouts";
import ErrorState from "components/common/ErrorState";
import {
  ConnectedAccountsPanel,
  ExternalCardSpendChart,
  ProgramInsightsPanel,
  ReceiptsMissingCard,
  SpendComparisonChart,
  TopCategoriesRadial,
  UnallocatedSpendCard,
} from "components/admin/byocDashboard";
import { useByocDashboard } from "hooks/useByocDashboard";

/**
 * Shell props shared by the loaded and error states.
 *
 * The background is transparent rather than `--color-bg-page` so the admin
 * shell's navy shows through, giving white cards on navy - the same figure and
 * ground as the legacy admin dashboard. That also removes the hard edge where
 * a page-coloured block would stop short of filling the outlet.
 *
 * `paddingTop` is pinned to zero, which is what `Page` already resolves to: it
 * ships horizontal and bottom padding only. Stating it makes the intent explicit
 * rather than inherited, because the admin shell drops straight from the navbar
 * into the outlet and the navbar's own bottom spacing is the gap above the first
 * row. If this page ever needs real breathing room at the top, this is the line
 * to change, and it needs a non-zero token (`--space-0` is `0px`).
 *
 * `color` is restated because `Page` declares `color: inherit`, so text outside
 * a card resolves against the scheme here rather than whatever an ancestor set.
 */
const pageProps = {
  sx: {
    paddingTop: "var(--space-0)",
    backgroundColor: "transparent",
    color: "var(--color-text-primary)",
  },
} as const;

/**
 * Admin dashboard for BYOC-only companies (/admin/dashboard).
 *
 * The legacy admin dashboard is built around the Spend app: an Outpave wallet,
 * a cash-flow graph, card issuing, card approvals, payments, rewards. A company
 * that links its own cards has none of that, so this is a different set of
 * panels rather than the same one with pieces hidden.
 *
 * Composition only. Every query and all the bucketing live in
 * `useByocDashboard`, except the two analytics-backed panels whose windows are
 * local to them.
 */
const ByocDashboard: React.FC = () => {
  const { t } = useTranslation();
  const {
    spend,
    weekly,
    missingReceipts,
    unallocated,
    accounts,
    transactions,
    loading,
    errors,
    hasFatalError,
    refetch,
  } = useByocDashboard();

  // Only a whole-page failure replaces the page. A single failed query leaves
  // its own panel empty while everything else still renders.
  if (hasFatalError) {
    return (
      <Page {...pageProps}>
        <ErrorState
          title={t("Something went wrong")}
          description={t("DashboardLoadError")}
          retryLabel={t("Retry")}
          onRetry={refetch}
        />
      </Page>
    );
  }

  return (
    <Page {...pageProps}>
      <Columns count={{ xs: 1, md: 2 }}>
        {/* `errors.compliance` is forwarded because both cards fall back to an
            empty ComplianceMetrics, and an empty backlog is indistinguishable
            from a clean one: without it a failed query renders "Every
            transaction has a receipt". */}
        <ReceiptsMissingCard
          metrics={missingReceipts}
          loading={loading.compliance}
          error={errors.compliance}
        />
        <UnallocatedSpendCard
          metrics={unallocated}
          loading={loading.compliance}
          error={errors.compliance}
        />
      </Columns>

      {/* The chart leads and takes the wider share: the trend is what the eye
          reads first, the account list is reference beside it.

          A fixed height rather than a minimum, because Connected accounts holds
          a ten-row transaction list. With only a minimum the row grew to fit all
          of it and the panel's own `overflow-y: auto` never engaged, so the
          section ran far past the chart beside it. Capping the row is what makes
          that list scroll. Applied at lg only: below that the two panels stack
          and each should size to its content. */}
      <Columns count={{ xs: 1, lg: 5 }} sx={{ height: { lg: "420px" } }}>
        <Stack sx={{ gridColumn: { lg: "1 / 4" }, minHeight: 0 }}>
          <ExternalCardSpendChart
            spend={spend}
            loading={loading.spend}
            error={errors.spend}
          />
        </Stack>
        <Stack sx={{ gridColumn: { lg: "4 / 6" }, minHeight: 0 }}>
          <ConnectedAccountsPanel
            accounts={accounts}
            transactions={transactions}
            currency={spend.currency}
            windowDays={spend.windowDays}
            loading={loading.accounts}
            transactionsLoading={loading.transactions}
          />
        </Stack>
      </Columns>

      {/* A fixed height rather than a min/max pair, for the same reason as the
          row above. A minimum lets the tallest panel set the height and stretches
          the others to it, which is how Program insights' five rows pulled this
          row past 700px; a maximum on top of that caps the container but not the
          grid items, so they overflow it instead of fitting. Pinned, all three
          panels get one definite height and each handles its own overflow: the
          insight list scrolls, both charts scale to their box. Applied at lg
          only: below that the panels stack and size to their content. */}
      <Columns count={{ xs: 1, md: 2, lg: 3 }} sx={{ height: { lg: "400px" } }}>
        <TopCategoriesRadial />
        <ProgramInsightsPanel />
        <SpendComparisonChart
          weekly={weekly}
          currency={spend.currency}
          loading={loading.spend}
        />
      </Columns>
    </Page>
  );
};

export default ByocDashboard;
